import { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { useTranslation } from "react-i18next";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Eye, EyeOff, Loader2, X, Shield } from "lucide-react";
import "@/i18n/fase6";

export default function IncidenciaDetailDrawer({ objectId, onClose, role }) {
  const { t } = useTranslation();
  // iter44 · RGPD P0 — TODOS los roles (incluido ADMIN) ven la imagen
  //   anonimizada por defecto. El original sólo se revela mediante acción
  //   explícita del usuario (click o botón), nunca por defecto.
  // iter46 · UX operacional — ADMIN/SUPERVISOR pueden previsualizar el
  //   original con HOVER (ephemeral, no audit) o CLICK (persistente + audit).
  //   Roles inferiores mantienen el flujo iter44 (sólo botón + audit).
  const canQuickReveal = role === "ADMIN" || role === "SUPERVISOR";
  const [showOriginal, setShowOriginal] = useState(false); // persistente (click)
  const [hoverReveal, setHoverReveal] = useState(false);   // ephemeral (hover)
  // iter46b · Ventana de 500ms tras "click de deshacer" en la que el hover
  //   NO puede reactivar la vista temporal. Evita el flicker cuando el
  //   cursor sigue sobre la imagen justo tras el click OFF.
  const [explicitlyHidden, setExplicitlyHidden] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [anonBlobUrl, setAnonBlobUrl] = useState(null);
  const canUnblur = canQuickReveal;
  const isRevealed = showOriginal || hoverReveal;

  const handleImageClick = () => {
    if (showOriginal) {
      // Deshacer: fuerza blur incluso si el cursor sigue encima.
      setShowOriginal(false);
      setHoverReveal(false);
      setExplicitlyHidden(true);
      setTimeout(() => setExplicitlyHidden(false), 500);
    } else {
      setShowOriginal(true); // audit se dispara vía useEffect
    }
  };

  useEffect(() => {
    // Reset a anonimizada al cambiar de detección o de rol.
    setShowOriginal(false);
    setHoverReveal(false);
    setExplicitlyHidden(false);
    setNote("");
    setImgError(false);
  }, [objectId, role]);

  const { data } = useSWR(objectId ? `/incidencias/${objectId}` : null, fetcher);

  // Registrar acceso a imagen original en audit_events (obligatorio RGPD).
  // iter46 · Sólo el reveal PERSISTENTE (showOriginal=true por click o botón)
  //   genera audit event. El hover ephemeral NO se audita — es una vista
  //   operacional transitoria equivalente a pasar el cursor por una foto.
  useEffect(() => {
    if (!objectId || !data?.detection) return;
    if (canQuickReveal && showOriginal) {
      client.post(`/incidencias/${objectId}/reveal-image`).catch(() => {});
    }
  }, [objectId, data?.detection?.object_id, canQuickReveal, showOriginal]);

  // Fetch autenticado de la imagen anonimizada como Blob → object URL.
  // El endpoint /api/incidencias/{id}/image requiere Authorization Bearer,
  // pero el <img src> del navegador no envía cabeceras — así que precargamos.
  // Se mantiene la anonimizada incluso durante `hoverReveal` para que el
  // regreso al blur al salir del cursor sea instantáneo (no vuelve a fetch).
  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    async function load() {
      if (!objectId) {
        setAnonBlobUrl(null);
        return;
      }
      try {
        const resp = await client.get(`/incidencias/${objectId}/image`, { responseType: "blob" });
        if (resp.status === 202 || !resp.data || (resp.data.size !== undefined && resp.data.size === 0)) {
          if (!cancelled) setImgError(true);
          return;
        }
        currentUrl = URL.createObjectURL(resp.data);
        if (!cancelled) {
          setAnonBlobUrl(currentUrl);
          setImgError(false);
        } else {
          URL.revokeObjectURL(currentUrl);
        }
      } catch {
        if (!cancelled) setImgError(true);
      }
    }
    load();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [objectId]);

  if (!objectId) return null;
  const d = data?.detection;

  const requestUnblur = async () => {
    try {
      await client.post(`/incidencias/${objectId}/reveal-image`);
      setShowOriginal(true);
      toast.success(t("fase4.inc_unblur_confirm"));
    } catch { toast.error("Error"); }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await client.post(`/incidencias/${objectId}/annotate`, { text: note, private: true });
      toast.success(t("fase4.inc_note_saved"));
      setNote("");
      mutate(`/incidencias/${objectId}`);
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="incidencia-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-lg bg-white border-l hairline overflow-y-auto">
        <div className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">Incidencia</div>
            <div className="font-serif text-lg text-salnes-ink">#{objectId}</div>
          </div>
          <button onClick={onClose} data-testid="incidencia-drawer-close" className="text-salnes-stone hover:text-salnes-ink">
            <X size={18} />
          </button>
        </div>
        {!d ? (
          <div className="p-6 text-sm text-salnes-stone">{t("fase4.loading")}</div>
        ) : (
          <div className="p-5 space-y-4 text-sm">
            {d.image_url && (
              <div className="rounded-xl overflow-hidden border hairline bg-black">
                {imgError && !isRevealed ? (
                  <div
                    className="w-full h-56 flex flex-col items-center justify-center text-white/80 text-xs bg-salnes-ink"
                    data-testid="incidencia-image-processing"
                  >
                    <Shield size={22} className="mb-2 text-salnes-emerald" />
                    <div className="font-medium">{t("fase4.inc_img_processing_title")}</div>
                    <div className="text-[10px] text-white/60 mt-1 px-6 text-center">
                      {t("fase4.inc_img_processing_sub")}
                    </div>
                  </div>
                ) : (
                  <div
                    className="relative"
                    data-testid="incidencia-image-wrapper"
                    data-reveal-mode={
                      showOriginal ? "persistent" : hoverReveal ? "hover" : "blur"
                    }
                    onMouseEnter={
                      canQuickReveal && !explicitlyHidden
                        ? () => setHoverReveal(true)
                        : undefined
                    }
                    onMouseLeave={
                      canQuickReveal ? () => setHoverReveal(false) : undefined
                    }
                    onClick={canQuickReveal ? handleImageClick : undefined}
                    style={{
                      cursor: canQuickReveal ? "zoom-in" : "default",
                    }}
                  >
                    <img
                      src={isRevealed ? d.image_url : (anonBlobUrl || undefined)}
                      alt={isRevealed ? "orixinal" : "anonimizada"}
                      data-testid="incidencia-image"
                      className={`w-full h-56 object-cover pointer-events-none ${!isRevealed && !anonBlobUrl ? "bg-salnes-ink animate-pulse" : ""}`}
                      draggable={false}
                      onLoad={() => setImgError(false)}
                      onError={() => setImgError(true)}
                    />
                    {canQuickReveal && hoverReveal && !showOriginal && (
                      <div
                        className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.15em] bg-black/70 text-white px-2 py-1 rounded pointer-events-none"
                        data-testid="incidencia-hover-hint"
                      >
                        {t("fase4.inc_hover_hint")}
                      </div>
                    )}
                    {showOriginal && (
                      <div
                        className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.15em] bg-salnes-coral text-white px-2 py-1 rounded pointer-events-none font-medium"
                        data-testid="incidencia-original-banner"
                      >
                        {t("fase4.inc_original_banner")}
                      </div>
                    )}
                  </div>
                )}
                {/* Chip informativo — SIEMPRE visible cuando showOriginal=false (persistente).
                    Se conserva incluso durante hoverReveal para que el usuario sepa que la
                    vista actual es transitoria y no un cambio de estado. */}
                {!showOriginal && (
                  <div
                    className="p-2 flex justify-between items-center bg-salnes-ink/95"
                    data-testid="incidencia-shield-chip"
                  >
                    <div className="text-white/70 text-[10px] px-2 flex items-center gap-1">
                      <Shield size={10} /> ANONIMIZADA (IA)
                    </div>
                    {canUnblur && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={requestUnblur}
                        data-testid="incidencia-unblur-btn"
                        className="text-white hover:bg-white/10 text-xs"
                      >
                        <Eye size={12} className="mr-2" />{t("fase4.inc_unblur")}
                      </Button>
                    )}
                  </div>
                )}
                {showOriginal && canUnblur && (
                  <div
                    className="p-2 flex justify-between items-center bg-salnes-ink/95"
                    data-testid="incidencia-shield-chip"
                  >
                    <div className="text-white/70 text-[10px] px-2 flex items-center gap-1">
                      <Shield size={10} /> ORIXINAL · acceso auditado
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setShowOriginal(false)}
                      data-testid="incidencia-unblur-btn"
                      className="text-white hover:bg-white/10 text-xs"
                    >
                      <EyeOff size={12} className="mr-2" />{t("fase4.inc_view_anonymized")}
                    </Button>
                  </div>
                )}
                <div className="px-3 py-2 bg-salnes-atlantic/5 border-t hairline">
                  <div className="text-[10px] text-salnes-stone flex items-center gap-1">
                    <Shield size={10} className="text-salnes-emerald" />
                    Imaxe anonimizada por IA · Modelo: Gemini Vision
                  </div>
                </div>
              </div>
            )}
            <Info label="Estado" value={<Badge variant="outline" className="text-[10px]">{d.workflow_status}</Badge>} />
            <Info label="Data" value={d.detection_date?.slice(0, 19).replace("T", " ")} />
            <Info label="Concello" value={d.municipality || "—"} />
            <Info label="Jetson" value={d.jetson_id ?? "—"} />
            <Info label="Coords" value={d.latitude && d.longitude ? `${d.latitude.toFixed(5)}, ${d.longitude.toFixed(5)}` : "—"} />
            <Info label="Tipo" value={d.overflow ? "Desborde" : "Observación"} />

            {canUnblur && (
              <div className="pt-4 border-t hairline">
                <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase4.inc_annotate")}</div>
                <Textarea
                  data-testid="incidencia-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("fase4.inc_note_placeholder")}
                  rows={3}
                />
                <Button
                  size="sm" onClick={saveNote} disabled={saving || !note.trim()}
                  data-testid="incidencia-note-save"
                  className="mt-2 bg-salnes-atlantic hover:bg-salnes-atlantic/90 rounded-full"
                >
                  {saving ? <Loader2 size={12} className="mr-2 animate-spin" /> : null}
                  {t("fase4.inc_note_save")}
                </Button>
              </div>
            )}

            {data?.notes?.length > 0 && (
              <div className="pt-4 border-t hairline space-y-2">
                {data.notes.map((n) => (
                  <div key={n.id} className="rounded-lg bg-salnes-sand/30 px-3 py-2 text-xs">
                    <div className="text-[10px] text-salnes-stone">{n.at?.slice(0, 16).replace("T", " ")} · {n.user_id} · {n.user_role}</div>
                    <div className="text-salnes-ink">{n.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{label}</div>
    <div className="text-sm text-salnes-ink text-right">{value}</div>
  </div>
);
